import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/** `blue` / `purple`: invoicing statuses (FA-Devis « Envoyé » #1D5FB8 on #E6F1FD, « Facturé » #6B3FC9 on #F1EAFE). */
export type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "muted" | "lime" | "dark" | "blue" | "purple";

const tones: Record<Tone, { box: string; dot: string }> = {
  neutral: { box: "bg-neutral text-ink-2", dot: "bg-subtle" },
  info: { box: "bg-primary-50 text-primary-700", dot: "bg-primary-500" },
  success: { box: "bg-success-bg text-success", dot: "bg-success" },
  warning: { box: "bg-warning-bg text-warning", dot: "bg-warning-500" },
  danger: { box: "bg-danger-bg text-danger", dot: "bg-danger-500" },
  muted: { box: "bg-neutral text-muted", dot: "bg-subtle" },
  lime: { box: "bg-lime-soft text-lime-text", dot: "bg-lime" },
  dark: { box: "bg-ink text-white", dot: "bg-lime" },
  blue: { box: "bg-[#E6F1FD] text-[#1D5FB8]", dot: "bg-[#1D5FB8]" },
  purple: { box: "bg-[#F1EAFE] text-[#6B3FC9]", dot: "bg-[#6B3FC9]" },
};

interface BadgeProps {
  tone?: Tone;
  /** "pill" for statuses, "tag" (radius 8) for roles and account states. */
  shape?: "pill" | "tag";
  size?: "sm" | "md";
  dot?: boolean;
  strike?: boolean;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function Badge({ tone = "neutral", shape = "pill", size = "md", dot, strike, icon, className, children }: BadgeProps) {
  const t = tones[tone];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 font-semibold whitespace-nowrap",
        shape === "pill" ? "rounded-full" : "rounded-lg",
        size === "md" ? "px-[11px] py-1 text-xs" : "px-2.5 py-[3px] text-[11px]",
        t.box,
        strike && "line-through",
        className,
      )}
    >
      {dot && <span className={cn("size-2.5 rounded-full", t.dot)} aria-hidden />}
      {icon}
      {children}
    </span>
  );
}

/** Small counter bubble, e.g. unread messages in the sidebar. */
export function CountBubble({ count, tone = "info", className }: { count: number; tone?: "info" | "danger"; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        "inline-flex min-w-5 h-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold",
        tone === "info" ? "bg-primary-500 text-white" : "bg-danger-bg text-danger",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}
